import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';

export default function ScoringConfig() {
  const [formData, setFormData] = useState({
    call_now: 80,
    warm: 60,
    track: 40,
    fit_weight: 70, // 0–100; signal = 100 - fit_weight
    where_now_confidence_floor: 60
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authService.getScoringConfig();
      const json = data.config_json || {};
      const buckets = json.buckets || json.thresholds || {};
      const weights = json.weights || {};
      const fitPct = Math.round((weights.fit ?? 0.7) * 100);
      setFormData({
        call_now: buckets.call_now ?? 80,
        warm: buckets.warm ?? 60,
        track: buckets.track ?? 40,
        fit_weight: fitPct,
        where_now_confidence_floor: Math.round((json.where_now_confidence_floor ?? 0.6) * 100)
      });
    } catch (err) {
      setError(err.message || 'Failed to load scoring settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const num = parseInt(value, 10);
    if (isNaN(num)) return;
    setFormData(prev => ({ ...prev, [name]: num }));
    setError('');
    setSuccess(false);
  };

  const handleFitSliderChange = (e) => {
    const fit = parseInt(e.target.value, 10);
    if (isNaN(fit)) return;
    setFormData(prev => ({ ...prev, fit_weight: fit }));
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      const fit = formData.fit_weight / 100;
      const signal = (100 - formData.fit_weight) / 100;
      await authService.updateScoringConfig({
        buckets: {
          call_now: formData.call_now,
          warm: formData.warm,
          track: formData.track
        },
        weights: { fit, signal },
        where_now_confidence_floor: formData.where_now_confidence_floor / 100
      });
      setSuccess(true);
      await loadConfig();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
          <span className="ml-3 text-gray-500">Loading scoring settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">Candidate scoring settings</h2>
      <p className="text-gray-600 mb-6">
        These settings control how candidates are ranked and labelled in your Candidate Graph and lists.
        You can change them at any time; the next scoring run will use the saved values.
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
          Settings saved. They will apply the next time candidate scores are calculated.
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Score bands */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Score bands</h3>
          <p className="text-sm text-gray-500 mb-4">
            Candidates get a single score from 0 to 100. Use these numbers to define when a candidate is shown as &quot;Call now&quot;, &quot;Warm&quot;, or &quot;Track&quot;. For example, with 80 / 60 / 40: anyone with 80 or above is &quot;Call now&quot;, 60–79 is &quot;Warm&quot;, 40–59 is &quot;Track&quot;, and below 40 is not in band.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="call_now" className="block text-sm font-medium text-gray-700 mb-1">
                Minimum score for &quot;Call now&quot;
              </label>
              <input
                id="call_now"
                name="call_now"
                type="number"
                min={0}
                max={100}
                value={formData.call_now}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00C6AE] focus:border-[#00C6AE]"
              />
            </div>
            <div>
              <label htmlFor="warm" className="block text-sm font-medium text-gray-700 mb-1">
                Minimum score for &quot;Warm&quot;
              </label>
              <input
                id="warm"
                name="warm"
                type="number"
                min={0}
                max={100}
                value={formData.warm}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00C6AE] focus:border-[#00C6AE]"
              />
            </div>
            <div>
              <label htmlFor="track" className="block text-sm font-medium text-gray-700 mb-1">
                Minimum score for &quot;Track&quot;
              </label>
              <input
                id="track"
                name="track"
                type="number"
                min={0}
                max={100}
                value={formData.track}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00C6AE] focus:border-[#00C6AE]"
              />
            </div>
          </div>
        </div>

        {/* Weights – single slider so fit + signal always 100% */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">What drives the score</h3>
          <p className="text-sm text-gray-500 mb-4">
            The overall score mixes how well a candidate fits your roles (fit) and how much recent activity we see (signals). Slide left to favour signals (recent news, job moves); slide right to favour fit (how well they match your roles). The two always add up to 100%.
          </p>
          <div className="max-w-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">More signals</span>
              <span className="text-sm font-medium text-gray-700">More fit</span>
            </div>
            <input
              id="fit_weight_slider"
              type="range"
              min={0}
              max={100}
              value={formData.fit_weight}
              onChange={handleFitSliderChange}
              className="w-full h-3 rounded-full appearance-none bg-gray-200 accent-[#00C6AE] cursor-pointer"
              style={{ accentColor: '#00C6AE' }}
            />
            <div className="flex justify-between mt-2 text-sm text-gray-600">
              <span>Fit: <strong>{formData.fit_weight}%</strong></span>
              <span>Signals: <strong>{100 - formData.fit_weight}%</strong></span>
            </div>
          </div>
        </div>

        {/* Where now confidence */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">&quot;Where are they now?&quot; confidence</h3>
          <p className="text-sm text-gray-500 mb-4">
            When we show an estimate of where a candidate works now, we only show it if we are at least this confident (0–100%). Higher values mean we show the estimate less often but with more certainty.
          </p>
          <div className="max-w-xs">
            <label htmlFor="where_now_confidence_floor" className="block text-sm font-medium text-gray-700 mb-1">
              Minimum confidence to show current employer (0–100%)
            </label>
            <input
              id="where_now_confidence_floor"
              name="where_now_confidence_floor"
              type="number"
              min={0}
              max={100}
              value={formData.where_now_confidence_floor}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00C6AE] focus:border-[#00C6AE]"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
